import React, { useCallback, useEffect, useState, useMemo } from "react";
import { ChantButton, ChantSelect } from "../../../../../../CommonComponents";
import "../styles/eventFormStyles.css";
import {
  getLocations,
  getParentLocations,
} from "../../../../../../services/firebaseService/endPoints/admin/content";
import useAdminDataStore, { selectCurrentGroupData } from "../../../../../../store/adminData/adminData";
import logger from "../../../../../../utils/logger";
import useContentFormParams from "../hooks/useContentFormParams";
import useDashboardInfo from "../../../../../../hooks/useDashboardInfo";

export const LocationSection = ({
  additionalLocations = [],
  onLocationChange,
}) => {
  const { formMode } = useContentFormParams();
  const { dashboardType } = useDashboardInfo();
  const groupData = useAdminDataStore(selectCurrentGroupData);
  const [allLocations, setAllLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isDisabled = useMemo(
    () => formMode === "view" || loading,
    [formMode, loading]
  );

  const fetchLocations = useCallback(async (groupId, isParent) => {
    if (!groupId) return;

    try {
      setLoading(true);
      setError(null);

      const locations =
        isParent && dashboardType === "clubadmin"
          ? await getParentLocations(groupId)
          : await getLocations(groupId);

      if (!Array.isArray(locations)) {
        throw new Error("Invalid locations data received");
      }

      // Deduplicate locations by ID to prevent duplicates
      const uniqueLocationsMap = new Map();
      locations.forEach((location) => {
        if (location?.id && !uniqueLocationsMap.has(location.id)) {
          uniqueLocationsMap.set(location.id, location);
        }
      });

      const uniqueLocations = Array.from(uniqueLocationsMap.values());

      const sortedLocations = [...uniqueLocations].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "")
      );

      setAllLocations(sortedLocations);
    } catch (err) {
      logger.error("Failed to fetch locations:", err);
      setError("Unable to load locations. Please try again.");
      setAllLocations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (groupData?.id) {
      fetchLocations(groupData.id, groupData?.isParent);
    }
  }, [groupData?.id, groupData?.isParent, fetchLocations]);

  const options = useMemo(() => {
    if (!allLocations.length) return [];

    const selectedIds = new Set(
      additionalLocations.filter((loc) => loc?.id).map((loc) => loc.id)
    );

    return allLocations
      .filter((location) => !selectedIds.has(location.id))
      .map((location) => ({
        label: location.name,
        value: location.id,
      }));
  }, [allLocations, additionalLocations]);

  // Get options for a specific select, including the currently selected location
  const getOptionsForSelect = useCallback(
    (currentLocationId) => {
      if (!allLocations.length) return [];

      const selectedIds = new Set(
        additionalLocations
          .filter((loc) => loc?.id && loc.id !== currentLocationId)
          .map((loc) => loc.id)
      );

      return allLocations
        .filter((location) => !selectedIds.has(location.id))
        .map((location) => ({
          label: location.name,
          value: location.id,
        }));
    },
    [allLocations, additionalLocations]
  );

  const handleLocationChange = useCallback(
    (value, index) => {
      if (!value || !allLocations.length) return;

      const selectedLocation = allLocations.find((loc) => loc.id === value);
      if (!selectedLocation) {
        logger.warn(`Location with id ${value} not found`);
        return;
      }

      const newLocations = [...additionalLocations];
      newLocations[index] = selectedLocation;

      onLocationChange?.(newLocations);
    },
    [allLocations, additionalLocations, onLocationChange]
  );

  const handleAddLocation = useCallback(() => {
    const newLocations = [...additionalLocations, null];
    onLocationChange?.(newLocations);
  }, [additionalLocations, onLocationChange]);

  const handleLocationRemove = useCallback(
    (index) => {
      if (additionalLocations.length <= 1) {
        logger.warn("Cannot remove the last location");
        return;
      }

      const newLocations = additionalLocations.filter(
        (_, idx) => idx !== index
      );
      onLocationChange?.(newLocations);
    },
    [additionalLocations, onLocationChange]
  );

  const handleLocationClear = useCallback(
    (index) => {
      const newLocations = [...additionalLocations];
      newLocations[index] = null;
      onLocationChange?.(newLocations);
    },
    [additionalLocations, onLocationChange]
  );

  if (error) {
    return (
      <div className="event-form-field-wrapper">
        <div style={{ color: "red", padding: "12px" }}>
          {error}
          <ChantButton
            type="secondary"
            onClick={() => fetchLocations(groupData?.id, groupData?.isParent)}
            style={{ marginLeft: "12px" }}
          >
            Retry
          </ChantButton>
        </div>
      </div>
    );
  }

  const locations =
    additionalLocations.length > 0 ? additionalLocations : [null];

  return (
    <div
      className="event-form-field-wrapper"
      style={{ display: "flex", flexDirection: "column", gap: "6px" }}
      role="group"
      aria-label="Event locations"
    >
      {locations.map((locationData, index) => {
        const canRemove = index > 0;
        const selectOptions = getOptionsForSelect(locationData?.id);

        return (
          <div
            key={`location-${index}`}
            style={{ display: "flex", gap: "12px", alignItems: "flex-end" }}
          >
            <div style={{ flex: 1 }}>
              <ChantSelect
                label={
                  locations.length > 1 ? `Location #${index + 1}` : "Location"
                }
                loading={loading}
                helpTooltip={
                  index === 0 ? (
                    <div style={{ width: 400 }}>
                      <p>
                        Locations added in the Content / Locations section of
                        the dashboard appear here for selection.
                      </p>
                      <p>
                        If event will take place across more than one location
                        you can add locations to a single event or create
                        multiple events. All addresses will be checked upon
                        member check-in.
                      </p>
                    </div>
                  ) : null
                }
                options={selectOptions}
                value={locationData?.id}
                onChange={(value) => handleLocationChange(value, index)}
                onClear={() => handleLocationClear(index)}
                placeholder="--Select Location--"
                disabled={isDisabled}
                allowClear={true}
                aria-label={`Location ${index + 1}`}
              />
            </div>

            {canRemove && (
              <ChantButton
                type="secondary"
                onClick={() => handleLocationRemove(index)}
                disabled={isDisabled}
                aria-label={`Remove location ${index + 1}`}
              >
                Remove
              </ChantButton>
            )}
          </div>
        );
      })}

      {!isDisabled && options.length > 0 && (
        <div style={{ marginTop: "4px" }}>
          <span>Create multi-location event? </span>
          <span
            role="button"
            tabIndex={0}
            style={{
              cursor: "pointer",
              textDecoration: "underline",
              color: "#1890ff",
            }}
            onClick={handleAddLocation}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleAddLocation();
              }
            }}
            aria-label="Add another location"
          >
            Add location.
          </span>
        </div>
      )}

      {!isDisabled && options.length === 0 && allLocations.length > 0 && (
        <div style={{ color: "#888", fontSize: "14px", marginTop: "4px" }}>
          All available locations have been selected.
        </div>
      )}
    </div>
  );
};
